/*
 *  -----------------------------------------------------------------------
 *    <copyright file="import-study-config.helper.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { TFunction } from 'i18next';
import {
    StudyConfigImportExportAction,
    StudyConfigImportExportStatus,
    StudyConfigurationImportType,
} from '@components/admin/import-study-config/import-study-config.interface';
import { LibraryGridColumn } from '@components/admin/library-grid/library-grid.interface';
import { useGridCells } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
// eslint-disable-next-line max-len
import { studyConfigImport_OpraMutations_studyConfigImport_ConfigImportResults_itemResults_ConfigItemImportResult } from '@queries/generated/studies.generated';

export const getISCTitles = (t: TFunction) => [
    `1. ${t('admin:fileUpload')}`,
    `2. ${t('admin:fileDetails')}`,
    `3. ${t('admin:importSummary')}`,
    `4. ${t('Import')}`,
];

export const getISCSubtitles = (t: TFunction) => ({
    1: t('admin:selectValidOpraFile'),
    2: t('admin:pleaseConfirmFile'),
    3: t('admin:pleaseReviewChanges'),
    4: '',
});

export const getISCNextBtnText = (t: TFunction) => ({
    1: t('common:add'),
    2: t('common:confirm'),
    3: t('common:confirm'),
    4: t('common:close'),
});

const getLogLine = (label: string, value?: string) => `${label}: ${value || ''}`;

export const generateFileDetails = (file: any, t: (text: string) => string) => {
    if (!file) return '';

    return [
        getLogLine(
            t('admin:exportedOn'),
            stringDateWithoutTimeFromUtc(file.ExportedUtcTimestamp, 'DD-MMM-YYYY') || undefined,
        ),
        getLogLine(t('admin:exportedBy'), file.ExportedBy),
        getLogLine(t('admin:exportVersion#'), file.FileFormatVersion),
        '\n',
        t('admin:source'),
        getLogLine(t('admin:organisationName'), file.Organisation),
        getLogLine(t('admin:studyName'), file.StudyConfig.Study.StudyNumber),
        getLogLine(t('admin:studyShortname'), file.StudyConfig.Study.ShortName),
        getLogLine(t('admin:studyConfigID'), file.StudyConfig.Study.ConfigId),
        getLogLine(t('admin:configHash'), file.FileHash),
    ].join('\n');
};

const { MaxLengthCell } = useGridCells();

export const getISCColumns = (t: (text: string) => string, showActions?: boolean) =>
    [
        ...(showActions
            ? [
                  {
                      title: t('ra:status'),
                      id: 'item-status',
                      field: 'status',
                      width: 80,
                      cells: { data: MaxLengthCell(150) },
                  },
              ]
            : []),
        {
            title: t('ra:action'),
            id: 'item-action',
            field: 'action',
            width: 80,
            cells: { data: MaxLengthCell(150) },
        },
        {
            title: t('ra:type'),
            id: 'item-type',
            field: 'type',
            width: 110,
            cells: { data: MaxLengthCell(150) },
        },
        {
            title: t('name'),
            id: 'item-name',
            field: 'name',
            width: 120,
            cells: { data: MaxLengthCell(150) },
        },
        {
            title: t('ra:message'),
            id: 'item-message',
            field: 'message',
            cells: { data: MaxLengthCell(150) },
        },
    ] as LibraryGridColumn[];

export const formISCData = (
    itemResults: studyConfigImport_OpraMutations_studyConfigImport_ConfigImportResults_itemResults_ConfigItemImportResult[],
) =>
    itemResults?.map((item) => ({
        status: StudyConfigImportExportStatus[item.status!],
        action: StudyConfigImportExportAction[item.action!],
        type: StudyConfigurationImportType[item.type!],
        name: item.name,
        message: item.message,
    })) || [];
